import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { NumberParam, StringParam, useQueryParam } from "use-query-params";
import {
  Select,
  StartPage,
} from "../../../../components";
import { useUi } from "../../../../context/ui.context";
import { messageTemplatesService } from "../../../../api";
import { usePageTitle } from "../../../../context/navigationContext";
import { icons } from "../../../../icons";
import { extractErrorMessage } from "../../../../utils/error-extractor";

type MessageTemplate = {
  id: number | string;
  name: string;
  title: string;
  category: string;
  category_display: string;
  language: string;
  language_display: string;
  target: string;
  target_category: string | null;
  type: string;
  disable_notification: boolean;
};

type MessageTemplateCreateOptions = {
  target: Record<string, string>;
  categories?: Array<{
    id: string | number;
    name: string;
    description: string;
  }>;
};

const DEFAULT_ORDERING = "name";

const MessageTemplatesList: FC = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { viewAlert } = useUi();
  usePageTitle(t("PAGE_TITLE_MESSAGE_TEMPLATES"));

  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [paginationData, setPaginationData] = useState<any>(null);
  const [createOptions, setCreateOptions] = useState<MessageTemplateCreateOptions | null>(null);

  const [page, setPage] = useQueryParam("page", NumberParam);
  const [ordering, setOrdering] = useQueryParam("ordering", StringParam);
  const [pageSize] = useQueryParam("page_size", NumberParam);
  const [searchString] = useQueryParam("search", StringParam);
  const [category, setCategory] = useQueryParam("category", StringParam);
  const [templateType, setTemplateType] = useQueryParam("type", StringParam);

  useEffect(() => {
    if (!ordering) {
      setOrdering(DEFAULT_ORDERING);
    }
  }, [ordering, setOrdering]);

  useEffect(() => {
    messageTemplatesService
      .getCreateOptions()
      .then((res) => {
        setCreateOptions(res.data as MessageTemplateCreateOptions);
      })
      .catch((error) => {
        console.error("Failed to load create options", error);
      });
  }, []);

  const updateList = useCallback(() => {
    setPageLoading(true);
    const params: Record<string, any> = {
      page: page || 1,
      page_size: pageSize || 10,
      search: searchString || "",
      category: category || undefined,
      type: templateType || undefined,
    };
    params.ordering = ordering || DEFAULT_ORDERING;
    messageTemplatesService
      .list(params)
      .then((res) => {
        const data = res.data;
        const objects = (data?.objects || []) as MessageTemplate[];
        setTemplates(objects);
        setPaginationData(data);
      })
      .catch((error) => {
        const message =
          error?.response?.data?.detail ||
          extractErrorMessage(error?.response?.data) ||
          t("FAILED_TO_LOAD_TEMPLATES");
        viewAlert({
          severity: "error",
          message,
        });
      })
      .finally(() => {
        setPageLoading(false);
      });
  }, [page, pageSize, ordering, searchString, category, templateType, t, viewAlert]);

  useEffect(() => {
    updateList();
  }, [updateList]);

  const getTargetLabel = useCallback(
    (value: string) => {
      if (!createOptions) {
        return value;
      }
      const entries = createOptions?.target || {};
      return entries?.[value] || value;
    },
    [createOptions]
  );

  const handleDelete = useCallback(
    (template: MessageTemplate) => {
      const confirmDelete = window.confirm(
        t("CONFIRM_DELETE_TEMPLATE_SET", {
          name: template.name,
        })
      );
      if (!confirmDelete) {
        return;
      }
      setPageLoading(true);
      messageTemplatesService
        .delete(template.id)
        .then(() => {
          viewAlert({
            severity: "information",
            message: t("TEMPLATE_DELETED"),
          });
          updateList();
        })
        .catch((error) => {
          const message =
            error?.response?.data?.detail ||
            extractErrorMessage(error?.response?.data) ||
            t("FAILED_TO_DELETE_TEMPLATE");
          viewAlert({
            severity: "error",
            message,
          });
        })
        .finally(() => {
          setPageLoading(false);
        });
    },
    [t, updateList, viewAlert]
  );

  const columns = useMemo(
    () => [
      {
        title: t("NAME"),
        key: "name",
        sorted: true,
      },
      {
        title: t("CATEGORY"),
        key: "category_display",
      },
      {
        title: t("TITLE"),
        key: "title",
      },
      {
        title: "",
        key: "props",
      },
    ],
    [t]
  );

  const items = useMemo(
    () => [
      {
        title: t("EDIT"),
        id: "edit",
        icon: icons.Edit,
        divider: false,
        onClick: (template: MessageTemplate) => {
          navigate(`/settings/message-templates/${template.id}/edit`);
        },
      },
      {
        title: t("DELETE"),
        id: "delete",
        icon: icons.DeleteIcon,
        divider: false,
        onClick: (template: MessageTemplate) => {
          handleDelete(template);
        },
        disabled: (template: MessageTemplate) => template.type === "system",
      },
    ],
    [t, navigate, handleDelete]
  );

  const tableRows = useMemo(
    () =>
      templates.map((template) => ({
        ...template,
        navigate: `/settings/message-templates/${template.id}/edit`,
        target_display: getTargetLabel(template.target),
      })),
    [templates, getTargetLabel]
  );

  const categoryOptions = useMemo(
    () => [
      { id: "", value: "", label: t("ALL") },
      ...((createOptions?.categories || []).map((item) => ({
        id: String(item.id),
        value: String(item.id),
        label: item.description || item.name,
      })) || []),
    ],
    [createOptions, t]
  );

  const typeOptions = useMemo(
    () => [
      { id: "", value: "", label: t("ALL") },
      { id: "custom", value: "custom", label: t("CUSTOM", { defaultValue: "Custom" }) },
      { id: "system", value: "system", label: t("SYSTEM", { defaultValue: "System" }) },
    ],
    [t]
  );

  const filters = (
    <div style={{ display: "flex", gap: "12px", width: "100%", flexWrap: "wrap" }}>
      <div style={{ minWidth: "220px", maxWidth: "320px", flex: "1 1 220px" }}>
        <Select
          title={t("CATEGORY")}
          data={categoryOptions}
          selectedValue={category || ""}
          onChange={(value: string) => {
            setPage(1);
            setCategory(value || undefined);
          }}
        />
      </div>
      <div style={{ minWidth: "220px", maxWidth: "320px", flex: "1 1 220px" }}>
        <Select
          title={t("TYPE")}
          data={typeOptions}
          selectedValue={templateType || ""}
          onChange={(value: string) => {
            setPage(1);
            setTemplateType(value || undefined);
          }}
        />
      </div>
    </div>
  );

  return (
    <StartPage
      title={t("MESSAGE_TEMPLATES")}
      noResourceText={t("NO_MESSAGE_TEMPLATES")}
      createResourceButton={t("CREATE_TEMPLATE")}
      createResourceLink="/settings/message-templates/create"
      rightButtonComponentCustomWithResources={filters}
      haveResource={templates.length > 0}
      pageLoading={pageLoading}
      tableData={{
        columns: columns,
        data: tableRows,
        hideCheckbox: true,
        props: items,
        paginationData: paginationData,
        updateData: updateList,
      }}
    />
  );
};

export default MessageTemplatesList;
